import { useMutation } from "@tanstack/react-query";
import { ButtonVariant, CoButton } from "components/CoButton";
import Dropdown, { CoDropdownItemProps } from "components/CoDropdown";
import { IconLoader } from "components/Icon";
import Tooltip from "components/Tooltip";
import { getPapersList, Paper, PaperDetails, SearchMode } from "helpers/api";
import { downloadCSV, downloadRIS } from "helpers/export";
import {
  MixpanelActionSource,
  MixpanelExportFileType,
} from "helpers/services/mixpanel/events";
import useAnalytics from "hooks/useAnalytics";
import useLabels from "hooks/useLabels";
import { useCallback } from "react";

type ExportButtonProps = {
  items: Paper[];
  title: string;
  disabled?: boolean;
  exportResultsEventOverride?: (
    fileType: MixpanelExportFileType,
    searchMode?: SearchMode
  ) => void; // TODO(Alan): Remove this once events follow feature event pattern like threads
  skipPapersFetch?: boolean;
  label?: string;
  actionSource?: MixpanelActionSource;
  searchMode?: SearchMode;
};

enum ExportFileType {
  CSV = "CSV",
  RIS = "RIS",
}

/**
 * @component ExportButton
 * @description Export a CSV file of search results
 * @param {Paper[]} items - The papers to export
 * @param {string} title - The title of the export
 * @param {boolean} disabled - Whether the button is disabled
 * @param {function} exportResultsEventOverride - Override the default export results event
 * @param {boolean} skipPapersFetch - Whether to skip fetching additional details
 * @param {string} label - The label of the export
 * @param {MixpanelActionSource} actionSource - The action source of the export
 */
const ExportButton = ({
  items,
  title,
  disabled,
  actionSource,
  exportResultsEventOverride,
  skipPapersFetch = false,
  label = undefined,
  searchMode = undefined,
}: ExportButtonProps) => {
  const { exportResultsEvent } = useAnalytics();
  const [searchLabels, exportLabels] = useLabels(
    "screens.search",
    "tooltips.export"
  );
  const getPapersListMutation = useMutation((ids: string[]) =>
    getPapersList({
      paper_ids: ids,
      options: {
        include_abstract: true,
        include_badges: true,
        include_journal: true,
      },
    })
  );

  const handleClickExport = useCallback(
    async (filetype: ExportFileType) => {
      const mixpanelExportFileType =
        filetype === ExportFileType.CSV
          ? MixpanelExportFileType.CSV
          : MixpanelExportFileType.RIS;

      if (exportResultsEventOverride) {
        exportResultsEventOverride(mixpanelExportFileType, searchMode);
      } else {
        exportResultsEvent(actionSource, mixpanelExportFileType);
      }

      try {
        if (skipPapersFetch) {
          // If skipPapersFetch is true, use the items directly
          const formattedPapers: PaperDetails[] = items.map((paper) => ({
            ...paper,
            id: paper.paper_id,
            abstract_takeaway: paper.display_text || "",
            provider_url: "",
            publish_date: "",
            journal:
              typeof paper.journal === "string"
                ? { title: paper.journal, scimago_quartile: undefined }
                : (paper.journal ?? { title: "", scimago_quartile: undefined }),
          }));

          if (filetype === ExportFileType.CSV) {
            downloadCSV(formattedPapers, title);
          } else {
            downloadRIS(formattedPapers, title);
          }
          return;
        }

        // Otherwise, fetch additional details
        const ids = items.map((item) => item.paper_id);
        const papersListData = await getPapersListMutation.mutateAsync(ids);
        const fullData = items
          .filter(
            (item) => item.paper_id in papersListData.paperDetailsListByPaperId
          )
          .map(
            (item) => papersListData.paperDetailsListByPaperId[item.paper_id]
          );

        if (filetype === ExportFileType.CSV) downloadCSV(fullData, title);
        else if (filetype === ExportFileType.RIS) downloadRIS(fullData, title);
      } catch (error) {
        console.error(error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      exportResultsEvent,
      getPapersListMutation,
      items,
      title,
      skipPapersFetch,
      actionSource,
      exportResultsEventOverride,
    ]
  );

  const dropdownItems: CoDropdownItemProps[] = [
    {
      key: ExportFileType.CSV,
      onClick: () => handleClickExport(ExportFileType.CSV),
      leftIcon: <i className="text-xl icon-download" />,
      title: exportLabels["csv"],
      description: exportLabels["csv-desc"],
    },
    {
      key: ExportFileType.RIS,
      onClick: () => handleClickExport(ExportFileType.RIS),
      leftIcon: <i className="text-xl icon-download" />,
      title: exportLabels["ris"],
      description: exportLabels["ris-desc"],
    },
  ];

  return getPapersListMutation.isLoading && !skipPapersFetch ? (
    <div className="inline-flex items-center w-[36px] mr-3">
      <IconLoader color="black" />
    </div>
  ) : (
    <Dropdown items={dropdownItems} title={exportLabels["header"]}>
      <Tooltip content={label ?? searchLabels["export-results"]}>
        <CoButton
          variant={ButtonVariant.Flat}
          icon="icon-download"
          iconRight="icon-chevron-down"
          size="sm"
          disabled={disabled || items.length < 1}
        />
      </Tooltip>
    </Dropdown>
  );
};

export default ExportButton;
